import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { GlobalContext } from 'contexts/GlobalContext';
import CartOrderCard from 'features/shop/cart/CartOrderCard';
import CartProductCard from 'features/shop/cart/CartProductCard';
import { sortSelectionsByTaxonomy } from 'features/shop/cart/utils';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import {
    getColdPackItems,
    getFrozenItems,
    getGroceryItems,
    getOrderMin,
    getTotalProductQty,
    individualizePerkSelections,
} from 'utils/orderUtils';
import { getProductsSubtotal, PriceType } from 'utils/priceUtils';

import CartFrozenItems from '../CartFrozenItems';

const GROUPED_SECTION_STYLES = 'mt-10 flex flex-col rounded-lg border border-off-white p-5';

function CartGroceryOrderCard({ account, selections }) {
    const navigate = useNavigate();
    const { setCartOpen } = useContext(GlobalContext);

    const { plusMembershipEligibleOrder, plusMembershipShortBranding } = useAccountPlusMembershipHook();

    const { nextOrder, proteinMinimum } = account;
    const { coldKeeperFee, frozenFee } = nextOrder.cartExperience;

    const individualizedPerkSelections = individualizePerkSelections(selections);
    const orderMin = getOrderMin(account);
    const groceryItems = getGroceryItems(selections);
    const groceryItemsWithoutColdPack = getGroceryItems(selections, coldKeeperFee > 0);
    // distinguish product source
    let allGroceryItems = coldKeeperFee > 0 ? groceryItemsWithoutColdPack : groceryItems;

    const frozenItems = getFrozenItems(selections);
    const frozenItemsQty = getTotalProductQty(frozenItems);

    if (frozenItems.length > 0) {
        allGroceryItems = allGroceryItems.filter((product) => !product.frozen);
    }

    const allGroceryItemsSorted = sortSelectionsByTaxonomy(allGroceryItems);

    const coldPackItems = getColdPackItems(individualizedPerkSelections);
    const coldPackItemsQty = getTotalProductQty(coldPackItems);

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const coldPackItemsTotalCost = getProductsSubtotal({
        options: { priceType },
        products: coldPackItems,
    });

    const diffToColdPackMin = proteinMinimum - coldPackItemsTotalCost;

    const groceryItemQty = getTotalProductQty(coldKeeperFee > 0 ? groceryItemsWithoutColdPack : groceryItems);

    const cartOrderCardTitle = `Groceries (${frozenItems.length > 0 ? groceryItemQty - frozenItemsQty : groceryItemQty})`;

    const coldPackItemsSorted = sortSelectionsByTaxonomy(coldPackItems);
    const frozenItemsSorted = sortSelectionsByTaxonomy(frozenItems);

    const groceryItemsTotalCost = getProductsSubtotal({
        options: { priceType },
        products: groceryItems,
    });

    const navigateToColdPackAisle = () => {
        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'ColdPack' }));
        } else {
            setCartOpen(false);
            navigate('/shop/aisle/cold+pack/all');
        }
    };

    const navigateToPDP = ({ isPerk, productId }) => {
        setCartOpen(false);
        navigate(`/shop/product/${productId}${isPerk ? '?perk=true' : ''}`, { state: { fromCart: true } });
    };

    return (
        <CartOrderCard title={cartOrderCardTitle} willShip={groceryItemsTotalCost >= orderMin}>
            <div className="mt-10 flex flex-col gap-30">
                {allGroceryItemsSorted.map((product, index) => {
                    const key = `${product.id}-${index}`;
                    return (
                        <CartProductCard
                            key={key}
                            plusMembershipEligibleOrder={plusMembershipEligibleOrder}
                            plusMembershipShortBranding={plusMembershipShortBranding}
                            onClick={() => navigateToPDP({ isPerk: product.isPerk, productId: product.id })}
                            product={product}
                            usePlusMemberPrice
                        />
                    );
                })}
            </div>
            {frozenItems.length > 0 && (
                <CartFrozenItems
                    className="mt-10 scroll-mt-10"
                    itemsContainerClassName={GROUPED_SECTION_STYLES}
                    frozenFee={frozenFee}
                    frozenItems={frozenItemsSorted}
                    frozenItemsQty={frozenItemsQty}
                    plusMembershipEligibleOrder={plusMembershipEligibleOrder}
                    plusMembershipShortBranding={plusMembershipShortBranding}
                    onProductClick={navigateToPDP}
                />
            )}
            {coldKeeperFee > 0 && coldPackItems.length > 0 && (
                <>
                    <p className="text-body-md mt-10 scroll-mt-10 font-grotesk-bold">Cold Pack ({coldPackItemsQty})</p>
                    <div className={GROUPED_SECTION_STYLES}>
                        {coldPackItemsTotalCost < proteinMinimum ? (
                            <Alert>
                                Add <span className="font-grotesk-bold">${diffToColdPackMin.toFixed(2)}</span> to waive
                                Cold Pack Fee
                            </Alert>
                        ) : (
                            <Alert variant="success">Your Cold Pack Fee is waived!</Alert>
                        )}
                        <p className="text-body-xs mt-10 text-grey">
                            ${coldKeeperFee.toFixed(2)} Cold Pack packaging fee applies; purchase at least $
                            {proteinMinimum.toFixed(2)} of Cold Pack items to waive the fee
                        </p>
                        <div className="my-10 flex flex-col gap-5">
                            {coldPackItemsSorted.map((product) => (
                                <CartProductCard
                                    key={product.id}
                                    plusMembershipEligibleOrder={plusMembershipEligibleOrder}
                                    plusMembershipShortBranding={plusMembershipShortBranding}
                                    onClick={() => navigateToPDP({ productId: product.id })}
                                    product={product}
                                    usePlusMemberPrice
                                />
                            ))}
                        </div>
                        {coldPackItemsTotalCost < proteinMinimum && (
                            <Button onClick={navigateToColdPackAisle} size="md" variant="secondary">
                                Shop Cold Pack
                            </Button>
                        )}
                        <p className="text-body-xs mt-10 text-grey">
                            Cold Pack items require specialized packaging to keep items refrigerator-cold in transit.
                        </p>
                    </div>
                </>
            )}
        </CartOrderCard>
    );
}

export default CartGroceryOrderCard;

CartGroceryOrderCard.propTypes = {
    account: PropTypes.shape({
        cartMinimum: PropTypes.number,
        nextOrder: PropTypes.shape({
            cartExperience: PropTypes.shape({
                coldKeeperFee: PropTypes.number,
                orderMinimum: PropTypes.number,
                orderMinimumOverridden: PropTypes.bool,
            }),
        }),
        proteinMinimum: PropTypes.number,
    }).isRequired,
    selections: PropTypes.shape({
        selected: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
};
